import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import api, { formatApiError } from "@/lib/api";
import { decideRequest } from "@/lib/approvals";
import { toast } from "sonner";
import { Check, X, ShieldCheck, ClipboardCheck } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const LABEL = {
  pan: "PAN",
  aadhaar: "Aadhaar",
  bank_name: "Bank name",
  bank_account: "Bank account",
  bank_ifsc: "IFSC",
  account_holder_name: "Account holder",
};

function mask(k, v) {
  if (v == null || v === "") return "—";
  if (k === "aadhaar") return `xxxx xxxx ${String(v).slice(-4)}`;
  if (k === "bank_account") return `${String(v).slice(0, 2)}${"x".repeat(Math.max(0, String(v).length - 6))}${String(v).slice(-4)}`;
  return String(v);
}

export default function ApprovalsPage({ embedded = false }) {
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState("pending");
  const [decideDialog, setDecideDialog] = useState(null); // {change, decision}
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/approvals", { params: { status: tab } });
      setRows(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [tab]);

  const decide = async () => {
    if (!decideDialog) return;
    setBusy(true);
    try {
      await decideRequest("approval", decideDialog.change.id, decideDialog.decision === "approved", note);
      toast.success(`Request ${decideDialog.decision}`);
      setDecideDialog(null); setNote("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      {!embedded && (
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight flex items-center gap-2">
          <ShieldCheck className="h-7 w-7 text-emerald-600" /> Approvals
        </h1>
        <p className="mt-1 text-sm text-zinc-500">Review employee-submitted PAN, Aadhaar and bank changes before they hit payroll.</p>
      </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-transparent p-0 border-b border-zinc-200 rounded-none h-auto">
          {["pending", "approved", "rejected", "all"].map((s) => (
            <TabsTrigger key={s} value={s} className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-emerald-600 data-[state=active]:text-emerald-700 data-[state=active]:shadow-none px-4 py-2 capitalize" data-testid={`approvals-tab-${s}`}>{s}</TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value={tab} className="mt-4">
          {rows.length === 0 ? (
            <Card className="border-zinc-200 shadow-none">
              <CardContent className="py-14 text-center">
                <ClipboardCheck className="h-9 w-9 text-zinc-300 mx-auto mb-2" />
                <div className="text-sm text-zinc-500">Nothing to review in "{tab}".</div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {rows.map((c) => (
                <Card key={c.id} className="border-zinc-200 shadow-none" data-testid={`approval-${c.id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div>
                        <div className="font-semibold text-zinc-900">{c.employee_name}</div>
                        <div className="text-xs text-zinc-500 mt-0.5">
                          {c.employee_code} · Submitted {dayjs(c.submitted_at).format("D MMM YYYY, HH:mm")}
                        </div>
                      </div>
                      <Badge variant="outline" className={`text-[11px] capitalize ${c.status === "pending" ? "bg-amber-50 text-amber-700 border-amber-200" : c.status === "approved" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : c.status === "rejected" ? "bg-red-50 text-red-700 border-red-200" : "bg-zinc-100 text-zinc-600"}`}>
                        {c.status}
                      </Badge>
                    </div>

                    <div className="mt-4 rounded-md border border-zinc-200 overflow-hidden">
                      <div className="grid grid-cols-[1fr_1fr_1fr] text-[11px] uppercase tracking-wider text-zinc-500 bg-zinc-50 px-4 py-2 font-medium">
                        <div>Field</div><div>Current (in payroll)</div><div>Proposed</div>
                      </div>
                      {Object.keys(c.proposed || {}).map((k) => (
                        <div key={k} className="grid grid-cols-[1fr_1fr_1fr] px-4 py-2 border-t border-zinc-100 text-sm">
                          <div className="text-zinc-600">{LABEL[k] || k}</div>
                          <div className="text-zinc-500 font-mono text-xs">{mask(k, c.previous?.[k])}</div>
                          <div className="text-emerald-700 font-medium font-mono text-xs">{mask(k, c.proposed[k])}</div>
                        </div>
                      ))}
                    </div>

                    {c.status === "pending" && (
                      <div className="mt-4 flex gap-2 justify-end">
                        <Button size="sm" variant="outline" onClick={() => setDecideDialog({ change: c, decision: "rejected" })} className="border-red-200 text-red-700 hover:bg-red-50" data-testid={`approval-reject-${c.id}`}>
                          <X className="h-4 w-4 mr-1" /> Reject
                        </Button>
                        <Button size="sm" onClick={() => setDecideDialog({ change: c, decision: "approved" })} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`approval-approve-${c.id}`}>
                          <Check className="h-4 w-4 mr-1" /> Approve
                        </Button>
                      </div>
                    )}

                    {c.status !== "pending" && (c.decision_note || c.decided_by_name) && (
                      <div className="mt-3 text-xs text-zinc-500">
                        {c.decided_by_name ? <>Decided by <span className="font-medium text-zinc-700">{c.decided_by_name}</span> · </> : null}
                        {c.decided_at ? dayjs(c.decided_at).format("D MMM YYYY, HH:mm") : ""}
                        {c.decision_note ? <div className="mt-1 italic">"{c.decision_note}"</div> : null}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={!!decideDialog} onOpenChange={(v) => !v && setDecideDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              {decideDialog?.decision === "approved" ? "Approve request" : "Reject request"}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-zinc-600">
            {decideDialog?.decision === "approved"
              ? `The new values will replace payroll data for ${decideDialog?.change?.employee_name}.`
              : `Please add a note so ${decideDialog?.change?.employee_name} knows what to fix.`}
          </p>
          <div>
            <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional note…" data-testid="approval-note" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDecideDialog(null)} className="border-zinc-300">Cancel</Button>
            <Button
              onClick={decide} disabled={busy}
              className={decideDialog?.decision === "approved" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"}
              data-testid="approval-confirm"
            >
              {busy ? "Saving…" : (decideDialog?.decision === "approved" ? "Approve" : "Reject")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
